import s from './LoreTwoProductAddToCart.module.scss'
import cn from 'clsx'

import {
  ProductOptions,
  RelatedProductOptions,
  AddMirror,
} from '@components/product'
import React, { FC, useEffect, useState } from 'react'
import type { Product, ProductVariant } from '@commerce/types/product'
import type { ModalInnerProps } from '@components/product/ModalInner/ModalInner'
import { Button, Container, Text, useUI } from '@components/ui'
import { getProductVariant, SelectedOptions } from '../helpers'
import { LoreTwo as ProductPage } from '@generated/model/models'
import useCart from '@framework/cart/use-cart'
import useRemoveItem from '@framework/cart/use-remove-item'
import usePrice from '@framework/product/use-price'
import type { LineItem } from '@commerce/types/cart'
import { useAddItem } from '@framework/cart'
import Script from 'next/script'

interface Props {
  className?: string
  page: ProductPage
  product: Product
  setModalProps: React.Dispatch<React.SetStateAction<ModalInnerProps>>
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  defaultOptions: SelectedOptions
}

export interface CartLine {
  [key: string]: {
    selectedOptions?: SelectedOptions
    variant?: ProductVariant
  }
}

const LoreTwoProductAddToCart: FC<Props> = ({
  page,
  product,
  className,
  setModalProps,
  setModalOpen,
  defaultOptions,
}) => {
  const [cartLines, setCartLines] = useState<CartLine>({})
  const [validCart, setValidCart] = useState(true)
  const [isRemoving, setIsRemoving] = useState(false)

  const removeItem = useRemoveItem()
  const addItem = useAddItem()

  const { setSidebarView, openSidebar } = useUI()

  const { data, isLoading, isEmpty } = useCart()

  const { price: total } = usePrice(
    data && {
      amount: Number(data.totalPrice),
      currencyCode: data.currency.code,
    }
  )

  // Clear cart on each page load
  useEffect(() => {
    const removeCart = async (lines: LineItem[]) => {
      for (let i = 0; i < lines.length; i++) {
        if (lines[i]) {
          try {
            await removeItem(lines[i])
          } catch (err) {
            console.log(err)
          }
        }
      }
    }
    if (
      data?.lineItems.length &&
      Object.keys(cartLines).length == 0 &&
      !isLoading &&
      !isRemoving
    ) {
      setIsRemoving(true)
      removeCart(data.lineItems)
    }

    if (Object.keys(cartLines).length == 0 && !isLoading && isEmpty) {
      const variant = getProductVariant(product, defaultOptions)
      if (variant) {
        setCartLines({
          [product.id]: defaultOptions,
        })

        addItem({
          productId: String(product.id),
          variantId: String(variant?.id),
          quantity: 1,
        })
      }
    }
  }, [
    data?.lineItems,
    removeItem,
    cartLines,
    isLoading,
    isEmpty,
    // addItem,
    product,
    isRemoving,
  ])

  const validateCart = (e: React.SyntheticEvent | undefined) => {
    if (Object.keys(cartLines).length == 0 || data?.lineItems.length === 0) {
      e && e.preventDefault()
      setValidCart(false)
      return false
    }
    if (
      !Object.keys(cartLines).find((lineId) => lineId == product.id) ||
      !data?.lineItems.find((item) => item.productId == product.id)
    ) {
      e && e.preventDefault()
      setValidCart(false)
      return false
    }
    setValidCart(true)
    return true
  }

  const rootClassName = cn(s.root, className)

  const totalStr = total && Object.keys(cartLines).length ? total : '$0.00'

  const priceAmount = product.price.value

  return (
    <div className={rootClassName}>
      <Container className={s.container}>
        <div className={s.totalColumn}>
          <Text variant={'smallText'} className={s.title}>
            Total
          </Text>
          <Text className={s.total}>{isEmpty ? ' ' : totalStr}</Text>
        </div>
        <div className={s.leadTimeColumn}>
          <Text variant={'smallText'} className={s.title}>
            Current Lead Time
          </Text>
          <Text className={s.leadtime}>{page.lead_time}</Text>
          <Text className={s.leadtimeMobile}>
            Current Lead Time {page.lead_time}
          </Text>
        </div>

        <div className={s.klarnaColumn}>
          <div className={s.klarnaPlacement}>
            <Script
              src="https://na-library.klarnaservices.com/lib.js"
              data-client-id="0518fe53-3ed2-59e9-8ea9-1b50368255a5"
              // onLoad={() => {
              //   window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' })
              // }}
            ></Script>
            <klarna-placement
              id="klarna-placement-loretwo"
              data-key="top-strip-promotion-badge"
              data-locale="en-US"
              data-purchase-amount={Math.ceil(priceAmount)}
              data-theme="dark"
            ></klarna-placement>
          </div>
        </div>
        <div className={s.buttonColumn}>
          <Button
            aria-label="Add to Cart"
            type="button"
            className={s.button}
            Component="a"
            // href="/checkout"
            // onClick={validateCart}
            onClick={() => {
              setSidebarView('CART_VIEW')
              openSidebar()
            }}
            disabled={Object.keys(cartLines).length == 0}
          >
            Add to Cart
          </Button>
        </div>
      </Container>
    </div>
  )
}

export default LoreTwoProductAddToCart
